import { Add } from '@carbon/react/icons';
import { Button, InlineNotification } from '@carbon/react';
import { type SearchParams } from 'atoms/search-atom';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useMemo, useReducer } from 'react';
import { CapacityResponse, EntityResponse } from 'types/api-responses';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { capacityCreatedAtom } from '../atoms';
import { useServerDrivenTable } from '../use-server-driven-table';
import { AddCapacityModal } from './add-capacity-modal';
import { EntityLinkProps } from './types';

type CapacityRow = CapacityResponse & Record<string, unknown>;

const DEFAULT_PARAMS: SearchParams = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortAttribute: 'subject_name',
  sortDirection: 'ASC',
};

const EntityLink = ({ entity, currentEntityId }: EntityLinkProps) => {
  return entity.id === currentEntityId ? (
    <span>{entity.name}</span>
  ) : (
    <Link to={entity.href}>{entity.name}</Link>
  );
};

const buildColumns = (
  currentEntityId: number
): DataTableColumnDef<CapacityRow>[] => [
  {
    id: 'subject_name',
    accessorFn: (row) => row.subject?.name ?? '',
    header: 'Who (Entity)',
    size: 260,
    cell: ({ row }) => {
      return row.original.subject ? (
        <EntityLink
          entity={row.original.subject}
          currentEntityId={currentEntityId}
        />
      ) : null;
    },
  },
  {
    id: 'predicate',
    accessorKey: 'predicate',
    header: 'Capacity',
    size: 180,
  },
  {
    id: 'object_name',
    accessorFn: (row) => row.object?.name ?? '',
    header: 'Target (Entity)',
    size: 260,
    cell: ({ row }) => {
      return row.original.object ? (
        <EntityLink
          entity={row.original.object}
          currentEntityId={currentEntityId}
        />
      ) : null;
    },
  },
];

interface CapacitiesProps {
  currentEntity: EntityResponse;
}

export const Capacities = ({ currentEntity }: CapacitiesProps) => {
  const [openModal, toggleOpenModal] = useReducer((s) => !s, false);
  const capacityCreated = useAtomValue(capacityCreatedAtom);
  const resetCapacityCreated = useResetAtom(capacityCreatedAtom);

  const { tableProps, reload } = useServerDrivenTable<CapacityRow>({
    href: currentEntity.resources.queries.capacities.href,
    defaultParams: DEFAULT_PARAMS,
  });

  const columns = useMemo(
    () => buildColumns(currentEntity.id),
    [currentEntity.id]
  );

  const addButton = (
    <Button
      key="add-capacity"
      size="sm"
      renderIcon={Add}
      onClick={() => {
        toggleOpenModal();
        resetCapacityCreated();
      }}
    >
      Add Capacity
    </Button>
  );

  return (
    <>
      {capacityCreated && (
        <InlineNotification
          lowContrast
          kind="success"
          title="Capacity created!"
        />
      )}
      <DataTable<CapacityRow>
        {...tableProps}
        columns={columns}
        getRowId={(row) => String(row.id)}
        initialTableState={{ sortColumn: 'subject_name', sortDirection: 'asc' }}
        searchPlaceholder="Search capacities"
        rightToolbarActions={[addButton]}
        config={{ columnFilters: false, columnReordering: false }}
        persistenceKey={`entity-${currentEntity.id}-capacities-table`}
      />
      <AddCapacityModal
        open={openModal}
        onClose={() => toggleOpenModal()}
        entity={currentEntity}
        getCapacities={reload}
      />
    </>
  );
};
